import { API_BASE_URL } from 'configs/AppConfig';

/** Max % while bytes are still uploading to NestJS. */
export const UPLOAD_BYTE_PROGRESS_MAX = 85;

/** Shown while NestJS processes the file and uploads to S3. */
export const UPLOAD_PROCESSING_PROGRESS = 92;

/**
 * Upload files to filemanager with XMLHttpRequest upload progress.
 * Byte upload maps to 0–85%. After bytes are sent, phase is "processing"
 * until the API responds with the S3 URL.
 */
export const uploadFilesWithProgress = ({
  files = [],
  accessToken,
  imageFor = '',
  thumbnailRequired,
  onProgress,
  signal,
}) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (imageFor) {
      formData.append('imageFor', imageFor);
    }
    if (thumbnailRequired != null && thumbnailRequired !== '') {
      formData.append('thumbnailRequired', thumbnailRequired);
    }

    let uploadBytesComplete = false;

    const report = (phase, percent) => {
      onProgress?.({ phase, percent });
    };
    const xhr = new XMLHttpRequest();
    const baseUrl = API_BASE_URL.replace(/\/+$/, '');
    xhr.open('POST', `${baseUrl}/filemanager/images/upload`);
    if (accessToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    }

    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable) {
        return;
      }

      const ratio = event.loaded / event.total;
      const percent = Math.min(
        UPLOAD_BYTE_PROGRESS_MAX,
        Math.round(ratio * UPLOAD_BYTE_PROGRESS_MAX),
      );
      report('uploading', percent);

      if (event.loaded >= event.total && !uploadBytesComplete) {
        uploadBytesComplete = true;
        report('processing', UPLOAD_PROCESSING_PROGRESS);
      }
    });

    xhr.addEventListener('load', () => {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText || '{}');
      } catch {
        reject(new Error('Upload failed'));
        return;
      }

      if (xhr.status === 401) {
        reject(
          Object.assign(new Error(data.message || 'Unauthorized'), {
            statusCode: 401,
          }),
        );
        return;
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        const message = Array.isArray(data.message)
          ? data.message.join(', ')
          : data.message || 'Upload failed';
        reject(new Error(message));
        return;
      }

      report('complete', 100);
      resolve(data.fileUrls || data.data || []);
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.addEventListener('abort', () =>
      reject(new DOMException('Aborted', 'AbortError')),
    );

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort());
    }

    xhr.send(formData);
  });
